import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import ValetApi from "../api/Api";
import ValetActivityTooltip from "./ValetActivityTooltip";
import { buildChartDateContextLabel } from "./chartDateContextLabel";
import "./Graph.css";

const COLORS = ["#93B249", "#0697f7"];

const ValetActivityAreaChartsSingle = ({ podiumId, selectedDate = null, startDate = null, endDate = null, userTimeZone = "UTC" }) => {
    const [activityData, setActivityData] = useState([]);
    const [error, setError] = useState(null);
    const dateContextLabel = buildChartDateContextLabel({ selectedDate, startDate, endDate });

    useEffect(() => {
        if (!podiumId) return;
        setError(null);

        const hasDateRange = Boolean(startDate && endDate);
        const request = hasDateRange ? ValetApi.getValetActivityByPodiumIdDateRange(podiumId, userTimeZone, startDate, endDate) : ValetApi.getTodayValetActivityByPodiumId(podiumId, userTimeZone, selectedDate);

        request
            .then((res) => {
                setActivityData(res?.data?.userTransactionCount || []);
            })
            .catch((err) => {
                setError(err?.response?.data?.error?.message || "No valet activity found for this podium.");
                setActivityData([]);
            });
    }, [podiumId, selectedDate, startDate, endDate, userTimeZone]);

    const chartData = activityData.map((u) => ({
        name: `${u.firstName} ${u.lastName.slice(0, 1)}.`,
        Parked: Number(u.parkedTransactionCount || 0),
        Retrieved: Number(u.retrievedTransactionCount || 0),
        Total: Number(u.totalTransactionCount || 0),
        firstParkTransactionTime: u.firstParkTransactionTime,
        lastParkTransactionTime: u.lastParkTransactionTime,
        firstRetrieveTransactionTime: u.firstRetrieveTransactionTime,
        lastRetrieveTransactionTime: u.lastRetrieveTransactionTime,
        firstTransactionTime: u.firstTransactionTime,
        lastTransactionTime: u.lastTransactionTime,
    }));

    if (!Array.isArray(chartData) || chartData.length === 0) {
        return <div className="valet-activity-no-data">No data available for this chart.</div>;
    }

    return (
        <div className="valet-activity-chart-row">
            <div className="valet-activity-chart-card">
                <h4 className="valet-activity-chart-title">Valet Activity (Area Chart)</h4>
                <div className="valet-activity-chart-context">Date: {dateContextLabel}</div>
                {error ? (
                    <div className="valet-activity-error">{error}</div>
                ) : (
                    <div className="valet-activity-chart-viewport">
                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                            minWidth={0}
                            debounce={300}>
                            <AreaChart
                                data={chartData}
                                margin={{ top: 42, right: 30, left: 10, bottom: 104 }}>
                                <XAxis
                                    dataKey="name"
                                    interval={0}
                                    minTickGap={0}
                                    tickMargin={12}
                                    height={58}
                                    angle={-18}
                                    textAnchor="end"
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    tick={{ fontSize: 14 }}
                                />
                                <Tooltip content={<ValetActivityTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={28}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="Parked"
                                    stroke={COLORS[0]}
                                    fill={COLORS[0]}
                                    fillOpacity={0.25}
                                    activeDot={{ r: 6 }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="Retrieved"
                                    stroke={COLORS[1]}
                                    fill={COLORS[1]}
                                    fillOpacity={0.25}
                                    activeDot={{ r: 6 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ValetActivityAreaChartsSingle;
